/**
 * Wind drift estimation — fluid-dynamics-grounded model for The Forge.
 *
 * Built on the Precision Cut Archery 2025 Arrow Ballistics Study (James Yates
 * + PCA, CC BY-NC-SA 4.0) measured drag constants and modeled drift inches,
 * plus Firenock's published airfoil mechanism + spin RPS numbers for AV2/AV3.
 *
 * The model has three layers:
 *
 *   1. Per-vane drift coefficient (relative to Bohning Blazer 3-fletch 2° hel
 *      = 1.000 baseline). Measured by PCA where available, engineering-
 *      estimated otherwise.
 *   2. Build modifier — fletch count (3/4/5) and helical offset stack onto
 *      the vane coefficient. PCA data: +9% per extra vane, +3.7% per +2° hel.
 *   3. Distance / wind / velocity scaling — drift ~ D² × Vw / V_eff^1.4.
 *
 * Sources:
 *   - PCA 2025 study: precisioncutarchery.com/research/arrow-study-2025
 *   - Yates writeup: westernhunter.net/information/the-arrow-ballistics-study-results/
 *   - Firenock Aerovane FAQ: firenock.com/aerovane-faq/
 *   - Miyazaki et al. Aerodynamic properties of an archery arrow, Sports Eng. 2013
 */

import type { Vane, VaneAirfoilTier } from '@/types';
import { lookupVaneDriftEntry } from '@/data/vane-drift-data';

// ─────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────

/**
 * Baseline: PCA 2025 — Bohning Blazer 3-fletch 2° helical at 280 fps,
 * 450 gr arrow, 10 mph crosswind:
 *   80 yd  → 9.33"
 *   120 yd → 21.49"
 * At 60 yd by quadratic scaling: 9.33 × (60/80)² ≈ 5.25".
 */
export const BASELINE_DRIFT_60YD_10MPH_INCHES = 5.25;
export const BASELINE_VELOCITY_FPS = 280;

// ─────────────────────────────────────────────────────────────────────────
// Vane drift coefficient lookup
// ─────────────────────────────────────────────────────────────────────────

/**
 * Get the drift coefficient for a vane.
 *   - If the vane catalog entry has an explicit coefficient → use it.
 *   - Otherwise fall back to airfoilTier defaults.
 *   - Otherwise return 1.0 (Blazer baseline).
 */
export function getVaneDriftCoefficient(vane: Vane | undefined): number {
  if (!vane) return 1.0;
  if (typeof vane.windDriftCoefficient === 'number') return vane.windDriftCoefficient;
  const entry = lookupVaneDriftEntry(vane.name);
  if (entry) return entry.coefficient;
  switch (vane.airfoilTier) {
    case 'airfoil':     return 0.85;
    case 'low-profile': return 0.93;
    case 'standard':    return 1.00;
    default:            return 1.00;
  }
}

/** Look up the airfoil tier for a vane (catalog field → name map → standard). */
export function getVaneAirfoilTier(vane: Vane | undefined): VaneAirfoilTier {
  if (!vane) return 'standard';
  if (vane.airfoilTier) return vane.airfoilTier;
  const entry = lookupVaneDriftEntry(vane.name);
  return entry?.airfoilTier ?? 'standard';
}

/** Did this vane have measured PCA data, or is the coefficient an estimate? */
export function isVanePcaTested(vane: Vane | undefined): boolean {
  if (!vane) return false;
  if (vane.pcaTested === true) return true;
  const entry = lookupVaneDriftEntry(vane.name);
  return entry?.pca === true;
}

// ─────────────────────────────────────────────────────────────────────────
// Build-level coefficient (combines vane + count + offset)
// ─────────────────────────────────────────────────────────────────────────

export interface BuildDriftInputs {
  /** Per-vane drift coefficient (from getVaneDriftCoefficient). */
  vaneCoefficient: number;
  /** Number of vanes fletched: 3, 4, or 5. */
  vaneCount: number;
  /** Helical / offset angle in degrees. 0 = straight, typical hunt = 2-4°. */
  helicalDeg: number;
}

/**
 * Per-build drift coefficient stacking vane + fletch count + helical.
 *
 *   PCA observation: going 3 → 4 fletch on the same vane added ~9% drift.
 *   PCA observation: going 2° → 4° helical on Blazer added ~3.7% drift.
 *
 * We model 5-fletch as +18% (proportional extrapolation; 5-fletch isn't in PCA's
 * dataset). Helical scales linearly above 2° baseline.
 */
export function buildDriftCoefficient({
  vaneCoefficient,
  vaneCount,
  helicalDeg,
}: BuildDriftInputs): number {
  const countMul = 1 + Math.max(0, vaneCount - 3) * 0.09;
  const helicalMul = 1 + 0.0185 * Math.max(0, helicalDeg - 2);
  return vaneCoefficient * countMul * helicalMul;
}

// ─────────────────────────────────────────────────────────────────────────
// Wind drift prediction at distance
// ─────────────────────────────────────────────────────────────────────────

export interface DriftPredictionInputs {
  /** Build coefficient from buildDriftCoefficient(). */
  buildCoefficient: number;
  /** Crosswind speed in mph. */
  crosswindMph: number;
  /** Distance to target in yards. */
  distanceYd: number;
  /** Arrow muzzle velocity (fps). Optional — slower arrow = more drift. */
  velocityFps?: number;
}

/**
 * Predict crosswind drift in inches at a given distance.
 *
 * Drift scales:
 *   - Linearly with crosswind speed (Vw)
 *   - Quadratically with distance (D²) — confirmed by PCA's 80→120yd data
 *   - Inversely with arrow velocity to the ~1.4 power (slower arrow spends
 *     more time in the wind; the lag-time formula gives this exponent)
 *   - Linearly with the build coefficient (vane + count + offset)
 */
export function predictWindDriftInches({
  buildCoefficient,
  crosswindMph,
  distanceYd,
  velocityFps,
}: DriftPredictionInputs): number {
  if (distanceYd <= 0 || crosswindMph === 0) return 0;
  const distScale = (distanceYd / 60) ** 2;
  const windScale = crosswindMph / 10;
  const velScale = velocityFps && velocityFps > 0
    ? (BASELINE_VELOCITY_FPS / velocityFps) ** 1.4
    : 1.0;
  return BASELINE_DRIFT_60YD_10MPH_INCHES * buildCoefficient * distScale * windScale * velScale;
}

// ─────────────────────────────────────────────────────────────────────────
// Spin rate prediction
// ─────────────────────────────────────────────────────────────────────────

export interface SpinInputs {
  /** Arrow muzzle velocity (fps). */
  velocityFps: number;
  /** Vane length in inches. Parsed from vane.length string. */
  vaneLengthIn: number;
  /** Helical / offset in degrees. */
  helicalDeg: number;
  /** Airfoil tier — drives a different mechanism for 'airfoil' vanes. */
  airfoilTier?: VaneAirfoilTier;
  /** Spin direction sign — purely for visualization. +1 = CW from shooter view. */
  direction?: 1 | -1;
}

/**
 * Predicted arrow spin rate in revolutions per second.
 *
 * For standard plastic vanes the spin is driven by air drag pushing on the
 * offset surface. The simplified formula:
 *
 *    distance_per_rev (in) = (360° / offset°) × vane_length (in)
 *    RPS = (velocity_fps × 12) / distance_per_rev
 *
 * Multiplied by an empirical 0.7 slip factor — the ideal formula assumes
 * 100% spin transfer, but real arrows lose energy to drag friction. Sport
 * Engineering literature converges on ~70% of theoretical.
 *
 * For airfoil vanes (Firenock AV2 / AV3), spin is generated by Bernoulli
 * lift on a rigid 3D shape, not air drag on an offset. Firenock published:
 *   AV3 at 300 fps → 90 revolutions in 20 yards → 450 RPS (27,000 RPM)
 *   AV2 at 300 fps → 60 revolutions in 20 yards → 300 RPS (18,000 RPM)
 * We scale linearly with velocity for airfoil tier.
 */
export function predictSpinRPS({
  velocityFps,
  vaneLengthIn,
  helicalDeg,
  airfoilTier,
}: SpinInputs): number {
  if (velocityFps <= 0) return 0;

  if (airfoilTier === 'airfoil') {
    // Firenock-class — AV3 is the high-spin reference. Scale on velocity.
    return (velocityFps / 300) * 450;
  }

  if (helicalDeg <= 0 || vaneLengthIn <= 0) return 0;

  const distPerRevInches = (360 / helicalDeg) * vaneLengthIn;
  const velocityInPerSec = velocityFps * 12;
  const idealRPS = velocityInPerSec / distPerRevInches;
  return idealRPS * 0.7;
}

// ─────────────────────────────────────────────────────────────────────────
// Vane length parsing helper
// ─────────────────────────────────────────────────────────────────────────

/** Extract a numeric inch value from strings like '2.0"', '1.85"', '2.7"'. */
export function parseVaneLengthInches(s: string | undefined): number {
  if (!s) return 0;
  const match = /([\d.]+)/.exec(s);
  return match ? parseFloat(match[1]) : 0;
}

// ─────────────────────────────────────────────────────────────────────────
// Convenience: turn a build state into a drift summary
// ─────────────────────────────────────────────────────────────────────────

export interface DriftSummary {
  /** Coefficient for this build (1.0 = Blazer 3×2°hel baseline). */
  buildCoefficient: number;
  /** Drift at 60 yd in a 10 mph crosswind, inches. */
  drift60yd10mph: number;
  /** Drift at 30 yd in a 10 mph crosswind, inches (typical whitetail distance). */
  drift30yd10mph: number;
  /** Drift at 100 yd in a 10 mph crosswind, inches (ELR / target). */
  drift100yd10mph: number;
  /** Estimated spin rate at muzzle, RPS. */
  spinRPS: number;
  /** Did this vane have measured PCA data, or is it an estimate? */
  pcaTested: boolean;
}

export function summarizeBuildDrift(args: {
  vane: Vane | undefined;
  vaneCount: number;
  helicalDeg: number;
  velocityFps: number;
}): DriftSummary {
  const vaneCoeff = getVaneDriftCoefficient(args.vane);
  const buildCoeff = buildDriftCoefficient({
    vaneCoefficient: vaneCoeff,
    vaneCount: args.vaneCount,
    helicalDeg: args.helicalDeg,
  });

  const drift30 = predictWindDriftInches({
    buildCoefficient: buildCoeff,
    crosswindMph: 10,
    distanceYd: 30,
    velocityFps: args.velocityFps,
  });
  const drift60 = predictWindDriftInches({
    buildCoefficient: buildCoeff,
    crosswindMph: 10,
    distanceYd: 60,
    velocityFps: args.velocityFps,
  });
  const drift100 = predictWindDriftInches({
    buildCoefficient: buildCoeff,
    crosswindMph: 10,
    distanceYd: 100,
    velocityFps: args.velocityFps,
  });

  const spinRPS = predictSpinRPS({
    velocityFps: args.velocityFps,
    vaneLengthIn: parseVaneLengthInches(args.vane?.length),
    helicalDeg: args.helicalDeg,
    airfoilTier: getVaneAirfoilTier(args.vane),
  });

  return {
    buildCoefficient: buildCoeff,
    drift60yd10mph: drift60,
    drift30yd10mph: drift30,
    drift100yd10mph: drift100,
    spinRPS,
    pcaTested: isVanePcaTested(args.vane),
  };
}
